/**
 * Converts regular URLs into embeddable iframe URLs
 * Supports 20+ platforms with rich embed previews
 */

export function getEmbedUrl(url) {
  if (!url) return null;
  const lower = url.toLowerCase();

  // YouTube (videos, shorts, playlists)
  if (lower.includes('youtube.com/watch') || lower.includes('youtu.be/')) {
    const videoId = extractYouTubeId(url);
    if (videoId) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}`, aspectRatio: '16/9' };
  }
  if (lower.includes('youtube.com/shorts/')) {
    const match = url.match(/shorts\/([a-zA-Z0-9_-]+)/);
    if (match) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${match[1]}`, aspectRatio: '9/16' };
  }
  if (lower.includes('youtube.com/playlist')) {
    const match = url.match(/list=([a-zA-Z0-9_-]+)/);
    if (match) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/videoseries?list=${match[1]}`, aspectRatio: '16/9' };
  }

  // Spotify (tracks, albums, playlists, episodes, shows, artists — not user profiles)
  if (lower.includes('open.spotify.com/') && /\/(track|album|playlist|episode|show|artist)\//.test(lower)) {
    // Add utm_source=generator to use Spotify's embed-generator path which is more stable
    const base = url.replace('open.spotify.com/', 'open.spotify.com/embed/');
    const separator = base.includes('?') ? '&' : '?';
    const embedPath = `${base}${separator}utm_source=generator&theme=0`;
    return { type: 'spotify', embedUrl: embedPath, aspectRatio: null, height: 352 };
  }

  // SoundCloud
  if (lower.includes('soundcloud.com/') && !lower.includes('soundcloud.com/search')) {
    return { type: 'soundcloud', embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`, aspectRatio: null, height: 450 };
  }

  // TikTok (videos)
  if (lower.includes('tiktok.com/') && lower.includes('/video/')) {
    const match = url.match(/video\/(\d+)/);
    if (match) return { type: 'tiktok', embedUrl: `https://www.tiktok.com/embed/v2/${match[1]}`, aspectRatio: '9/16', height: 740 };
  }

  // Twitch (channels, clips, VODs)
  if (lower.includes('twitch.tv/')) {
    const parent = window.location.hostname;
    // Clips
    const clipMatch = url.match(/twitch\.tv\/\w+\/clip\/([a-zA-Z0-9_-]+)/) || url.match(/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/);
    if (clipMatch) {
      return { type: 'twitch', embedUrl: `https://clips.twitch.tv/embed?clip=${clipMatch[1]}&parent=${parent}`, aspectRatio: '16/9' };
    }
    // VODs
    const vodMatch = url.match(/twitch\.tv\/videos\/(\d+)/);
    if (vodMatch) {
      return { type: 'twitch', embedUrl: `https://player.twitch.tv/?video=${vodMatch[1]}&parent=${parent}`, aspectRatio: '16/9' };
    }
    // Live channels
    const channelMatch = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)\/?$/);
    if (channelMatch) {
      return { type: 'twitch', embedUrl: `https://player.twitch.tv/?channel=${channelMatch[1]}&parent=${parent}`, aspectRatio: '16/9' };
    }
  }

  // Apple Music
  if (lower.includes('music.apple.com/')) {
    const embedUrl = url.replace('music.apple.com', 'embed.music.apple.com');
    return { type: 'applemusic', embedUrl, aspectRatio: null, height: 450 };
  }

  // Deezer
  if (lower.includes('deezer.com/')) {
    const match = url.match(/deezer\.com\/(?:\w+\/)?(track|album|playlist)\/(\d+)/);
    if (match) return { type: 'deezer', embedUrl: `https://widget.deezer.com/widget/dark/${match[1]}/${match[2]}`, aspectRatio: null, height: 300 };
  }

  // Vimeo
  if (lower.includes('vimeo.com/')) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${match[1]}?badge=0&autopause=0`, aspectRatio: '16/9' };
  }

  // Dailymotion
  if (lower.includes('dailymotion.com/video/') || lower.includes('dai.ly/')) {
    const match = url.match(/(?:dailymotion\.com\/video\/|dai\.ly\/)([a-zA-Z0-9]+)/);
    if (match) return { type: 'dailymotion', embedUrl: `https://www.dailymotion.com/embed/video/${match[1]}`, aspectRatio: '16/9' };
  }

  // Mixcloud
  if (lower.includes('mixcloud.com/') && !lower.includes('/discover')) {
    return { type: 'mixcloud', embedUrl: `https://www.mixcloud.com/widget/iframe/?feed=${encodeURIComponent(url)}&hide_cover=1`, aspectRatio: null, height: 180 };
  }

  // Bandcamp (albums, tracks)
  if (lower.includes('bandcamp.com/') && (lower.includes('/album/') || lower.includes('/track/'))) {
    return { type: 'bandcamp', embedUrl: `https://bandcamp.com/EmbeddedPlayer/${lower.includes('/album/') ? 'album' : 'track'}=${extractBandcampSlug(url)}/size=large/bgcol=333333/linkcol=e99708/tracklist=false/transparent=true/`, aspectRatio: null, height: 350 };
  }

  // Audiomack
  if (lower.includes('audiomack.com/')) {
    const match = url.match(/audiomack\.com\/([^/]+)\/(song|album|playlist)\/([^/?]+)/);
    if (match) return { type: 'audiomack', embedUrl: `https://audiomack.com/embed/${match[2]}/${match[1]}/${match[3]}`, aspectRatio: null, height: 252 };
  }

  // Tidal
  if (lower.includes('tidal.com/')) {
    const match = url.match(/tidal\.com\/(?:browse\/)?(track|album|playlist|video)\/([a-zA-Z0-9-]+)/);
    if (match) return { type: 'tidal', embedUrl: `https://embed.tidal.com/${match[1]}s/${match[2]}`, aspectRatio: null, height: 300 };
  }

  // Loom
  if (lower.includes('loom.com/share/')) {
    const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (match) return { type: 'loom', embedUrl: `https://www.loom.com/embed/${match[1]}`, aspectRatio: '16/9' };
  }

  // Streamable
  if (lower.includes('streamable.com/')) {
    const match = url.match(/streamable\.com\/([a-zA-Z0-9]+)/);
    if (match) return { type: 'streamable', embedUrl: `https://streamable.com/e/${match[1]}`, aspectRatio: '16/9' };
  }

  // CodePen
  if (lower.includes('codepen.io/')) {
    const match = url.match(/codepen\.io\/([^/]+)\/(?:pen|full)\/([a-zA-Z0-9]+)/);
    if (match) return { type: 'codepen', embedUrl: `https://codepen.io/${match[1]}/embed/${match[2]}?default-tab=result&theme-id=dark`, aspectRatio: '16/9' };
  }

  // Pinterest
  if (lower.includes('pinterest.com/pin/') || lower.includes('pin.it/')) {
    const match = url.match(/pin\/(\d+)/) || url.match(/pin\.it\/([a-zA-Z0-9]+)/);
    if (match) return { type: 'pinterest', embedUrl: `https://assets.pinterest.com/ext/embed.html?id=${match[1]}`, aspectRatio: null, height: 500 };
  }

  // Kick (live streams)
  if (lower.includes('kick.com/')) {
    const match = url.match(/kick\.com\/([a-zA-Z0-9_]+)\/?$/);
    if (match) return { type: 'kick', embedUrl: `https://player.kick.com/${match[1]}`, aspectRatio: '16/9' };
  }

  // Rumble
  if (lower.includes('rumble.com/')) {
    const match = url.match(/rumble\.com\/embed\/([a-zA-Z0-9]+)/);
    if (match) return { type: 'rumble', embedUrl: `https://rumble.com/embed/${match[1]}/`, aspectRatio: '16/9' };
  }

  // Facebook Video
  if ((lower.includes('facebook.com/') || lower.includes('fb.watch/')) && (lower.includes('/videos/') || lower.includes('fb.watch'))) {
    return { type: 'facebook', embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`, aspectRatio: '16/9' };
  }

  // Reddit
  if (lower.includes('reddit.com/') && lower.includes('/comments/')) {
    return { type: 'reddit', embedUrl: `${url.split('?')[0]}?ref=share&ref_source=embed`, aspectRatio: null, height: 500 };
  }

  // Figma
  if (lower.includes('figma.com/') && (lower.includes('/file/') || lower.includes('/proto/') || lower.includes('/design/'))) {
    return { type: 'figma', embedUrl: `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`, aspectRatio: '16/9' };
  }

  // Google Maps
  if (lower.includes('google.com/maps') || lower.includes('maps.google.com') || lower.includes('goo.gl/maps')) {
    return { type: 'googlemaps', embedUrl: `https://maps.google.com/maps?q=${encodeURIComponent(url)}&output=embed`, aspectRatio: '16/9' };
  }

  return null;
}

function extractYouTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function extractBandcampSlug(url) {
  // Returns the URL path for bandcamp embed (used as identifier)
  try {
    const u = new URL(url);
    return u.pathname.split('/').pop();
  } catch {
    return '';
  }
}

export function isEmbeddableUrl(url) {
  return getEmbedUrl(url) !== null;
}

/**
 * Returns a human-friendly label for the embed type
 */
export function getEmbedLabel(url) {
  const embed = getEmbedUrl(url);
  if (!embed) return null;
  const labels = {
    youtube: 'YouTube Video',
    spotify: 'Spotify Player',
    soundcloud: 'SoundCloud Player',
    tiktok: 'TikTok Video',
    twitch: 'Twitch Stream',
    applemusic: 'Apple Music',
    deezer: 'Deezer Player',
    vimeo: 'Vimeo Video',
    dailymotion: 'Dailymotion Video',
    mixcloud: 'Mixcloud Mix',
    bandcamp: 'Bandcamp Player',
    audiomack: 'Audiomack Player',
    tidal: 'Tidal Player',
    loom: 'Loom Video',
    streamable: 'Streamable Video',
    codepen: 'CodePen',
    pinterest: 'Pinterest Pin',
    kick: 'Kick Stream',
    rumble: 'Rumble Video',
    facebook: 'Facebook Video',
    reddit: 'Reddit Post',
    figma: 'Figma Design',
    googlemaps: 'Google Maps'
  };
  return labels[embed.type] || 'Embedded Content';
}
